/**
 * The neck, when the music under the caret is written in tablature.
 *
 * Typing already writes tab -- a digit is a fret there, and the arrows move between strings
 * -- so what the panel is for is the rest of it: seeing where the selected notes are taken,
 * reaching the frets above nine without the two-digit run, and moving a passage up the neck
 * onto another string without changing a note of it.
 *
 * It acts on the selection, as every section below it does, and the two rows act in opposite
 * directions: a string keeps the note and moves the hand, a fret keeps the hand and changes
 * the note. The lit buttons are where the selection already is, so a passage that sits in one
 * position says so before anything is moved. A string that cannot reach one of the selected
 * notes refuses in words and the page shows them, which is the answer every other button here
 * gives.
 */

import { HIGHEST_FRET, tab } from "@app/score-editor";
import type { StringTuning } from "@app/score-editor";
import { Kicker } from "./Page";
import type { ScoreActions } from "../lib/score-actions";

/** Every fret the editing layer will place, which is as far as an ordinary neck goes. */
const FRETS = [...Array(HIGHEST_FRET + 1).keys()];

/** What a string is called here: its number, and the note it is tuned to. */
function openNote(open: StringTuning): string {
  const alter = open.alter > 0 ? "♯".repeat(open.alter) : "♭".repeat(-open.alter);
  return `${open.step}${alter}${open.octave}`;
}

export interface ScorePaletteTabProps {
  readonly actions: ScoreActions;
}

export default function ScorePaletteTab({ actions }: ScorePaletteTabProps): JSX.Element | null {
  const { score, selection, caret } = actions;
  const tuning =
    tab.tuningOf(score, caret?.partId ?? null) ?? tab.tuningInSelection(score, selection);
  if (tuning === null) return null;

  const taken = tab.positionsInSelection(score, selection);
  const strings = new Set(taken.map((place) => place.string));
  const frets = new Set(taken.map((place) => place.fret));
  const lit = "border-ink bg-ink text-white";
  const plain = "border-rule text-ink-faded hover:border-ink-quiet hover:text-ink";

  return (
    <div className="space-y-1.5">
      <Kicker>Tab</Kicker>
      <p className="text-[11px] leading-snug text-ink-faded">
        Where the selected notes are taken: a string moves the hand, a fret moves the note.
      </p>
      <div className="grid grid-cols-6 gap-1">
        {tuning.map((open, at) => {
          const string = at + 1;
          return (
            <button
              key={string}
              type="button"
              title={`String ${string}, tuned to ${openNote(open)}`}
              aria-pressed={strings.has(string)}
              onClick={() =>
                actions.edit(tab.setString(score, selection, string), `string ${string}`)
              }
              className={`flex h-8 flex-col items-center justify-center rounded-[8px] border leading-none ${strings.has(string) ? lit : plain}`}
            >
              <span className="text-[11px]">{string}</span>
              <span className="text-[9px] opacity-70">{openNote(open)}</span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-9 gap-1">
        {FRETS.map((fret) => (
          <button
            key={fret}
            type="button"
            title={fret === 0 ? "The open string" : `Fret ${fret}`}
            aria-pressed={frets.has(fret)}
            onClick={() => actions.edit(tab.setFret(score, selection, fret), `fret ${fret}`)}
            className={`flex h-7 items-center justify-center rounded-[6px] border font-mono text-[11px] ${frets.has(fret) ? lit : plain}`}
          >
            {fret}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => actions.edit(tab.refret(score, selection), "refret")}
        className="w-full rounded-[8px] border border-rule px-2 py-1 text-[12px] text-ink-faded hover:border-ink-quiet hover:text-ink"
      >
        Lowest positions
      </button>
    </div>
  );
}
